import React, { useState, useEffect } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { HelpCircle } from 'lucide-react';

const FormBuilderTour = ({ run, onComplete }) => {
  const [stepIndex, setStepIndex] = useState(0);

  const steps = [
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Welcome to Fylo Form Builder!👋</h3>
          <p className="text-gray-700 mb-3">
            Let's take a quick tour to help you create amazing forms. This will only take a minute!
          </p>
          <p className="text-sm text-gray-500">
            You can skip this tour anytime by clicking "Skip" or pressing ESC.
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="form-title"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Form Title</h3>
          <p className="text-gray-700">
            Click here to give your form a custom title. Choose something descriptive that tells respondents what your form is about!
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="undo-redo"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Undo & Redo</h3>
          <p className="text-gray-700 mb-2">
            Made a mistake? No worries! Use these buttons to undo or redo your changes.
          </p>
          <p className="text-sm text-blue-600">
            💡Tip: You can also use <strong>Ctrl+Z</strong> to undo and <strong>Ctrl+Y</strong> to redo.
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="delete-all"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Delete All Questions</h3>
          <p className="text-gray-700">
            Need to start fresh? This button deletes all questions at once. Don't worry - you can always undo it!
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="preview"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Preview Your Form</h3>
          <p className="text-gray-700">
            See exactly how your form will look to respondents. Test it out before publishing!
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="publish"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Publish Your Form</h3>
          <p className="text-gray-700">
            Once you're happy with your form, click here to publish it and start collecting responses!
          </p>
        </div>
      ),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="add-question"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Add Questions</h3>
          <p className="text-gray-700 mb-2">
            Click here to add questions to your form. Choose from 17 different question types!
          </p>
          <p className="text-sm text-gray-600">
            Short text, multiple choice, ratings, scales, and many more...
          </p>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tour="ai-import"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">AI-Powered Import</h3>
          <p className="text-gray-700 mb-2">
            Have questions ready? Just paste them here, and Fylo's AI will automatically create your form!
          </p>
          <p className="text-sm text-blue-600">
            💡This saves you tons of time - just paste and go!
          </p>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: '[data-tour="question-settings"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Question Settings</h3>
          <p className="text-gray-700 mb-2">
            When you select a question, customize it here:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
            <li>Change question type</li>
            <li>Mark as required</li>
            <li>Set point values for gamification</li>
            <li>Choose entrance animations</li>
          </ul>
        </div>
      ),
      placement: 'left',
      disableBeacon: true,
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">You're All Set! 🎉</h3>
          <p className="text-gray-700 mb-3">
            You now know the basics of creating forms with Fylo. Start building your first form and explore all the features!
          </p>
          <p className="text-sm text-gray-600 mb-2">
            Need help? Click the help button anytime to restart this tour.
          </p>
          <p className="text-sm text-blue-600">
            Happy form building! 
          </p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
  ];

  const handleJoyrideCallback = (data) => {
    const { status, action, index, type } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // Tour completed or skipped
      if (onComplete) {
        onComplete();
      }
    } else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      // Move to next step
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setStepIndex(nextStepIndex);
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      stepIndex={stepIndex}
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#0066FF',
          textColor: '#374151',
          backgroundColor: '#FFFFFF',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          arrowColor: '#FFFFFF',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 12,
          padding: 20,
          fontSize: 14,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: '#0066FF',
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: 14,
          fontWeight: 600,
        },
        buttonBack: {
          color: '#6B7280',
          marginRight: 10,
        },
        buttonSkip: {
          color: '#9CA3AF',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
      disableScrolling={false}
      spotlightPadding={4}
      disableOverlayClose
      hideCloseButton={false}
    />
  );
};

export default FormBuilderTour;
